/**
 * Pontos do globo a partir das regiões e cidades da Home (fora do GlobeMap.tsx, para o arquivo
 * do componente só exportar componentes).
 */
import { distanceKm } from './municipalities';
import type { CitySummary, RegionSummary } from './homeData';

export { distanceKm };

export interface MapPoint {
  key: string;
  name: string;
  lat: number;
  lng: number;
  businesses: number;
  listsCount: number;
  /** Texto ao lado do nome na etiqueta ("3 listas"), já traduzido. */
  note: string;
}

/** Pontos por região ("Florianópolis e região"). */
export function regionPoints(
  regions: RegionSummary[],
  label: (region: RegionSummary) => string,
  note: (lists: number) => string,
): MapPoint[] {
  return regions.map((r) => ({
    key: r.key,
    name: label(r),
    lat: r.lat,
    lng: r.lng,
    businesses: r.businesses,
    listsCount: r.lists,
    note: note(r.lists),
  }));
}

/** Pontos por cidade (de perto, no mergulho do globo). */
export function cityPoints(cities: CitySummary[], note: (lists: number) => string): MapPoint[] {
  return cities.map((c) => ({
    key: c.key,
    name: c.name,
    lat: c.lat,
    lng: c.lng,
    businesses: c.businesses,
    listsCount: c.listIds.length,
    note: note(c.listIds.length),
  }));
}

/** Maior distância entre dois pontos (km): mede o quanto as listas estão espalhadas. */
export function spreadKm(points: MapPoint[]): number {
  let max = 0;
  for (const a of points) for (const b of points) max = Math.max(max, distanceKm(a, b));
  return max;
}

function shuffle<T>(items: T[]): T[] {
  const list = [...items];
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j]!, list[i]!];
  }
  return list;
}

/**
 * Paradas do passeio da câmera: até `count` pontos sorteados, de preferência a `minKm` uns dos
 * outros, para a câmera andar de verdade entre eles.
 */
export function pickTourStops(points: MapPoint[], count: number, minKm: number): MapPoint[] {
  const pool = shuffle(points);
  const farFrom = (p: MapPoint, picked: MapPoint[]): boolean =>
    picked.every((q) => distanceKm(p, q) >= minKm);
  const picked: MapPoint[] = [];
  for (const p of pool) if (picked.length < count && farFrom(p, picked)) picked.push(p);
  for (const p of pool) if (picked.length < count && !picked.includes(p)) picked.push(p);
  return picked;
}
